import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Award, Lock, Play } from 'lucide-react';
import type { MyCourse } from '@mlm/shared';
import { learn } from '../lib/api';
import { subscribedCreators } from '../lib/course-access';
import { qk } from '../lib/queries';
import { CoachButton, Eyebrow, ErrorNote, Loading, Screen } from '../components/primitives';
import { LearnNav } from '../components/learn-nav';
import { MySubscriptions } from '../components/my-subscriptions';

/**
 * The courses this member is enrolled in, and their creator subscriptions.
 * Browsing and buying live one step over, under Browse.
 */
export function LearnPage() {
  const q = useInfiniteQuery({
    queryKey: qk.myCourses,
    queryFn: ({ pageParam }) => learn.myCourses(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 60_000,
  });
  const courses = q.data?.pages.flatMap((p) => p.items) ?? [];
  // A subscription enrolls nothing until a course is opened, so a new
  // subscriber lands here with no courses: point them at what it opens. Shares
  // the cache with the Subscriptions block above.
  const subs = useQuery({
    queryKey: qk.mySubscriptions,
    queryFn: learn.subscriptions,
    staleTime: 60_000,
  });
  const subscribed = subscribedCreators(subs.data).size > 0;

  return (
    <Screen eyebrow="Learn" title="Your courses" subtitle="Everything you are enrolled in." wide>
      <LearnNav />
      <MySubscriptions />
      {q.isPending ? (
        <Loading label="Loading your courses" />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : courses.length === 0 ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>{subscribed ? 'Ready when you are' : 'Nothing yet'}</Eyebrow>
          <p className="mt-2 text-[0.93rem] text-ink/70">
            {subscribed
              ? 'Your subscription is on. Open any course marked Included under Browse to start.'
              : 'You are not enrolled in any course.'}
          </p>
          <Link to="/learn/browse" className="mt-6 inline-block">
            <CoachButton>Browse courses</CoachButton>
          </Link>
        </div>
      ) : (
        <>
          <ul>
            {courses.map((c) => (
              <CourseRow key={c.courseId} course={c} />
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="px-5 py-6">
              <CoachButton
                block
                onClick={() => void q.fetchNextPage()}
                disabled={q.isFetchingNextPage}
              >
                {q.isFetchingNextPage ? 'Loading' : 'Show more'}
              </CoachButton>
            </div>
          )}
        </>
      )}
    </Screen>
  );
}

function CourseRow({ course }: { course: MyCourse }) {
  // A past `expiresAt` locks the card exactly like a revocation does.
  const expired = course.expiresAt != null && new Date(course.expiresAt).getTime() < Date.now();
  const locked = course.revokedAt != null || expired;
  const pct = Math.max(0, Math.min(100, Math.round(course.progressPct)));

  const body = (
    <div className="flex items-start gap-4 px-5 py-5">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-border bg-surface text-sage-deep">
        {locked ? (
          <Lock className="h-5 w-5" aria-hidden />
        ) : course.completed ? (
          <Award className="h-5 w-5 text-gold" aria-hidden />
        ) : (
          <Play className="h-5 w-5" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[0.95rem] leading-snug text-ink">{course.productName}</p>
        <p className="mt-1 text-[0.85rem] text-sage-deep">
          {locked ? 'Access ended' : `${course.completedCount} of ${course.lessonCount} lessons`}
        </p>
        {!locked && (
          <div className="mt-3 h-1 w-full bg-border" role="presentation">
            <div className="h-1 bg-verde" style={{ width: `${pct}%` }} />
          </div>
        )}
        {!locked && course.resumeLessonTitle && (
          <p className="mt-2 truncate text-[0.85rem] text-ink/60">
            Next: {course.resumeLessonTitle}
          </p>
        )}
      </div>
    </div>
  );

  if (locked) return <li className="border-b border-border opacity-60">{body}</li>;
  return (
    <li className="border-b border-border">
      <Link
        to={`/learn/${course.courseId}`}
        className="block transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-verde"
      >
        {body}
      </Link>
    </li>
  );
}
